'use client';

import * as React from 'react';
import useEmblaCarousel, {
    type UseEmblaCarouselType,
} from 'embla-carousel-react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
    opts?: CarouselOptions;
    plugins?: CarouselPlugin;
    orientation?: 'horizontal' | 'vertical';
    setApi?: (api: CarouselApi) => void;
};

// The carousel wrapper renders an ARIA landmark region, which must have an
// accessible name. Require either aria-label or aria-labelledby at the call
// site rather than defaulting one here — a shared default would give every
// carousel on a page the same name, breaking the "unique name per carousel" rule.
// Include the word "carousel" in the label.
type CarouselAccessibleName =
    | { 'aria-label': string; 'aria-labelledby'?: never }
    | { 'aria-labelledby': string; 'aria-label'?: never };

type CarouselContextProps = {
    carouselRef: ReturnType<typeof useEmblaCarousel>[0];
    api: ReturnType<typeof useEmblaCarousel>[1];
    scrollPrev: () => void;
    scrollNext: () => void;
    canScrollPrev: boolean;
    canScrollNext: boolean;
    hiddenSlides: number[];
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

/** Slide position, supplied by CarouselContent so a CarouselItem knows which slide it is. */
const CarouselSlideIndexContext = React.createContext<number | null>(null);

// A slide flush against the viewport edge can overlap it by a rounding error; embla's own
// slidesInView() counts that as visible, but nothing of the slide actually shows.
const MIN_VISIBLE_SLIVER_PX = 4;

const findHiddenSlides = (api: NonNullable<CarouselApi>, orientation: 'horizontal' | 'vertical') => {
    const root = api.rootNode().getBoundingClientRect();

    // Nothing laid out (jsdom, `display: none` ancestor) — measurements would hide every slide.
    if (root.width === 0 && root.height === 0) {
        return [];
    }

    return api
        .slideNodes()
        .map((slide, index) => {
            const box = slide.getBoundingClientRect();
            const visible = orientation === 'horizontal'
                ? Math.min(box.right, root.right) - Math.max(box.left, root.left)
                : Math.min(box.bottom, root.bottom) - Math.max(box.top, root.top);

            return { index, visible };
        })
        .filter(slide => slide.visible < MIN_VISIBLE_SLIVER_PX)
        .map(slide => slide.index);
};

const sameSlides = (left: number[], right: number[]) =>
    left.length === right.length && left.every((index, position) => index === right[position]);

function useCarousel() {
    const context = React.useContext(CarouselContext);

    if (!context) {
        throw new Error('useCarousel must be used within a <Carousel />');
    }

    return context;
}

const Carousel = React.forwardRef<
    HTMLDivElement,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'aria-label' | 'aria-labelledby'>
    & CarouselProps
    & CarouselAccessibleName
>(
    (
        {
            orientation = 'horizontal',
            opts,
            setApi,
            plugins,
            className,
            children,
            ...props
        },
        ref,
    ) => {
        const [carouselRef, api] = useEmblaCarousel(
            {
                ...opts,
                axis: orientation === 'horizontal' ? 'x' : 'y',
            },
            plugins,
        );
        const [canScrollPrev, setCanScrollPrev] = React.useState(false);
        const [canScrollNext, setCanScrollNext] = React.useState(false);
        const [hiddenSlides, setHiddenSlides] = React.useState<number[]>([]);

        const onSelect = React.useCallback((api: CarouselApi) => {
            if (!api) {
                return;
            }

            setCanScrollPrev(api.canScrollPrev());
            setCanScrollNext(api.canScrollNext());
        }, []);

        const scrollPrev = React.useCallback(() => {
            api?.scrollPrev();
        }, [api]);

        const scrollNext = React.useCallback(() => {
            api?.scrollNext();
        }, [api]);

        const handleKeyDown = React.useCallback(
            (event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    scrollPrev();
                }
                else if (event.key === 'ArrowRight') {
                    event.preventDefault();
                    scrollNext();
                }
            },
            [scrollPrev, scrollNext],
        );

        React.useEffect(() => {
            if (!api || !setApi) {
                return;
            }

            setApi(api);
        }, [api, setApi]);

        React.useEffect(() => {
            if (!api) {
                return;
            }

            onSelect(api);
            api.on('reInit', onSelect);
            api.on('select', onSelect);

            return () => {
                api?.off('select', onSelect);
            };
        }, [api, onSelect]);

        React.useEffect(() => {
            if (!api) {
                return;
            }

            const update = () => setHiddenSlides((previous) => {
                const next = findHiddenSlides(api, orientation);

                // Same array when nothing moved, so scroll traffic does not re-render slides.
                return sameSlides(previous, next) ? previous : next;
            });

            const events = ['slidesInView', 'settle', 'select', 'resize', 'slidesChanged', 'reInit'] as const;

            update();
            events.forEach(event => api.on(event, update));

            return () => events.forEach(event => api.off(event, update));
        }, [api, orientation]);

        return (
            <CarouselContext.Provider
                value={{
                    carouselRef,
                    api: api,
                    opts,
                    orientation:
            orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
                    scrollPrev,
                    scrollNext,
                    canScrollPrev,
                    canScrollNext,
                    hiddenSlides,
                }}
            >
                <div
                    ref={ref}
                    onKeyDownCapture={handleKeyDown}
                    className={cn('relative', className)}
                    role="region"
                    {...props}
                >
                    {children}
                </div>
            </CarouselContext.Provider>
        );
    },
);
Carousel.displayName = 'Carousel';

const CarouselContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & {
        overlay?: React.ReactNode;
        viewportClassName?: string;
    }
>(({ className, viewportClassName, overlay, children, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();

    return (
        <div
            ref={carouselRef}
            // `overlay` children render inside this viewport so pointer events on
            // them bubble to Embla's drag root, keeping swipe working over them.
            className={cn('overflow-hidden', overlay && 'relative', viewportClassName)}
            style={orientation === 'vertical' ? { overflowX: 'visible', overflowY: 'clip' } : undefined}
        >
            <div
                ref={ref}
                className={cn(
                    'flex',
                    orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
                    className,
                )}
                {...props}
            >
                {/* Providers render no DOM, so slides stay direct children of the track embla
                    measures. Only elements are counted — a `false` child would shift the indexes. */}
                {(() => {
                    let index = 0;

                    return React.Children.map(children, child =>
                        React.isValidElement(child)
                            ? (
                                    <CarouselSlideIndexContext.Provider value={index++}>
                                        {child}
                                    </CarouselSlideIndexContext.Provider>
                                )
                            : child,
                    );
                })()}
            </div>
            {overlay}
        </div>
    );
});
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
    const { orientation, hiddenSlides } = useCarousel();
    const slideIndex = React.useContext(CarouselSlideIndexContext);

    // Out-of-view slides stay in the DOM; `inert` keeps every descendant, including ones
    // mounted while hidden, out of the tab order.
    const isOutOfView = slideIndex !== null && hiddenSlides.includes(slideIndex);

    return (
        <div
            ref={ref}
            role="group"
            aria-hidden={isOutOfView || undefined}
            inert={isOutOfView}
            className={cn(
                'min-w-0 shrink-0 grow-0 basis-full',
                orientation === 'horizontal' ? 'pl-4' : 'pt-4',
                className,
            )}
            {...props}
        />
    );
});
CarouselItem.displayName = 'CarouselItem';

const CarouselPrevious = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<typeof Button>
>(({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();

    return (
        <Button
            ref={ref}
            variant={variant}
            size={size}
            className={cn(
                'absolute  h-8 w-8 rounded-full',
                orientation === 'horizontal'
                    ? '-left-12 top-1/2 -translate-y-1/2'
                    : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
                className,
            )}
            disabled={!canScrollPrev}
            onClick={scrollPrev}
            {...props}
        >
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Previous slide</span>
        </Button>
    );
});
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<typeof Button>
>(({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();

    return (
        <Button
            ref={ref}
            variant={variant}
            size={size}
            className={cn(
                'absolute h-8 w-8 rounded-full',
                orientation === 'horizontal'
                    ? '-right-12 top-1/2 -translate-y-1/2'
                    : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
                className,
            )}
            disabled={!canScrollNext}
            onClick={scrollNext}
            {...props}
        >
            <ArrowRight className="h-4 w-4" />
            <span className="sr-only">Next slide</span>
        </Button>
    );
});
CarouselNext.displayName = 'CarouselNext';

export {
    type CarouselApi,
    type CarouselOptions,
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
};
